import React, { useState, useEffect, useRef } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import { getReadableTime, getReadableTimeOnly } from "../../../utils/converter";
import AudioPlayer from "./AudioPlayer";
import NoData from "../../common/NoData";
import Loading from "../../common/loading";
import Interpretation from "./InterpretationData";
import jsPDF from "jspdf";
import "./keyar-layout.scss";
import Grid from "@mui/material/Grid";
import * as htmlToImage from "html-to-image";
import Cookie from "js-cookie";
import { styled } from "@mui/material/styles";
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  ListItemText,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useLogo } from "../../../context/LogoContext";

import TraceThicknessDropdown from "./dropdown";

const StyledSelect = styled(Select)({
  "& .MuiPaper-root": {
    backgroundColor: "#faf6ff", // This will change the background color of the dropdown
  },
  borderRadius: "4px",
  border: "0.5px solid #BFBFBF",
  background: "#fff",
  width: "9.375rem",
  "& .MuiSelect-select": {
    height: "1rem",
    padding: "0.625rem",
  },
});

const KeyarGraph = ({
  data,
  deviceName,
  isPlaying,
  setIsPlaying,
  basicDetail,
  otherDetail,
  placeDetail,
  selectedSubTab,
}) => {
  //console.log(basicDetail)

  const cookieData = Cookie.get("data");
  const [isKeyarEcho, setIsKeyarEcho] = useState(false);
  const [isKeyarDT, setIsKeyarDT] = useState(false);
  const [isKeyarCM, setIsKeyarCM] = useState(false);
  const [interpretationData, setInterpretationData] = useState({});
  const [generatedTime, setGeneratedTime] = useState("");
  const [difference, setDifference] = useState("");
  const [fileURL, setFileURL] = useState(null);
  const [fhr, setFHr] = useState([]);
  const [mhr, setMHR] = useState([]);
  const [uc, setUC] = useState([]);
  const [fhr2, setFHR2] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [fmm, setFmm] = useState([]);
  const [spo2, setSpo2] = useState([]);
  const [afm, setAfm] = useState([]);
  const [isLoading, setisLoading] = useState(false);
  const [keyarData, setKeyarData] = useState({});
  const [download, setDownload] = useState(false);
  const [interpretationDisable, setInterpretationDisable] = useState("");
  const [isDeviceData, setIsDeviceData] = useState();
  const [nstTime, setNstTime] = useState(data?.data_time);
  const [isMrn, setIsMrn] = useState(); // For Malaysia Specific Requirement
  const [traceThickness, setTracethickness] = useState(1.25); // Default to 1.25 for trace Thickness
  const [isUserSelected, setIsUserSelected] = useState(false); // Track if the user has made a selection
  const [accountType, setAccountType] = useState("");
  const { finalLogo, setLogo, setHospitalName } = useLogo();

  const tracethicknessOptions = ["1.00", "1.25", "1.50", "1.75", "2.00"];

  const getPatientIdentifier = () => {
    if (
      basicDetail?.mrn &&
      basicDetail.mrn !== "NA" &&
      basicDetail.mrn !== "-" &&
      basicDetail.mrn.trim() !== ""
    ) {
      return { label: "Patient MRN", value: basicDetail.mrn };
    }
    return { label: "Patient ID", value: basicDetail?.id };
  };

  const { label, value } = getPatientIdentifier();

  const handleTraceThicknessChange = (event) => {
    setTracethickness(event.target.value);
    setIsUserSelected(true); // Update that user has made a selection
  };

  useEffect(() => {
    if (cookieData) {
      const parsedData = JSON.parse(cookieData);
      const algorithmEnabled = parsedData.fetal_distress_algorithm;
      setIsMrn(algorithmEnabled);
      setAccountType(parsedData.account_type);
    }
  }, [cookieData]);

  useEffect(() => {
    if (deviceName == "Keyar DT") {
      setIsKeyarDT(true);
    } else if (deviceName == "Keyar CM") {
      setIsKeyarCM(true);
    } else if (deviceName == "Keyar Echo") {
      setIsKeyarEcho(true);
    }

    const keyardata = data?.params;
    const start_time = data?.data_time;
    let startTime =
      String(start_time).length === 10 ? start_time : start_time / 1000;

    if (!startTime) {
      // Broken/incomplete sessions can report data_time as 0, which would
      // otherwise anchor the chart window and monitoring-duration
      // calculations to the Unix epoch instead of the actual recording.
      // Fall back to the earliest timestamp actually present in the
      // recorded params.
      const recordedTimestamps = keyardata
        ?.flatMap((param) => Object.keys(param?.data_set || {}))
        .map((key) =>
          String(key).length === 13 ? Number(key) / 1000 : Number(key),
        )
        .filter((timestamp) => Number.isFinite(timestamp) && timestamp > 0);
      if (recordedTimestamps?.length) {
        startTime = Math.min(...recordedTimestamps);
      }
    }
    setStartTime(startTime);
    const interpretation_data = data?.fetal_distress_processed_data;

    const temp_fileURL = data?.file_url;
    setFileURL(temp_fileURL);

    let interpretation_for_fhr1;
    let interpretation_for_fhr2;
    let fhr2_signal_present;
    if (interpretationData) {
      if (
        "fhr1_data" in interpretation_data &&
        "fhr2_data" in interpretation_data
      ) {
        interpretation_for_fhr1 =
          interpretation_data.fhr1_data.fetal_distress_data;
        interpretation_for_fhr2 =
          interpretation_data.fhr2_data.fetal_distress_data;
        fhr2_signal_present = interpretation_data.fhr2_data.signal_present;
      }
    } else {
      interpretation_for_fhr1 = interpretation_data;
      interpretation_for_fhr2 = {};
      fhr2_signal_present = false;
    }

    setInterpretationData({
      interpretation_for_fhr1,
      interpretation_for_fhr2,
      fhr2_signal_present,
    });

    //To Remove Any Duplicate param type
    // const keyar = keyardata?.reduce((accumulator, obj) => {
    //     const foundObject = accumulator.find(item => item.param_type === obj.param_type);
    //     if (!foundObject) {
    //       accumulator.push(obj);
    //     }
    //     return accumulator;
    // }, []);

    const sortTimestamp = (data) => {
      return Object.fromEntries(
        Object.entries(data || []).sort((a, b) => a[0] - b[0]),
      );
    };

    const fhrData =
      keyardata?.find((obj) => obj.param_type === "fhr")?.data_set || {};
    const fhr2Data =
      keyardata?.find((obj) => obj.param_type === "fhr_two")?.data_set || {};
    const mhrData =
      keyardata?.find((obj) => obj.param_type === "mhr")?.data_set || {};
    const ucData =
      keyardata?.find((obj) => obj.param_type === "uc")?.data_set || {};
    const spo2Data =
      keyardata?.find((obj) => obj.param_type === "spo2")?.data_set || {};

 // Helper function from ChartLayout2
const normalizeToSeconds = (ts) => {
  const num = Number(ts);
  if (!num || isNaN(num)) return null;
  if (String(num).length === 13) return Math.floor(num / 1000);
  if (String(num).length === 10) return num;
  return null;
};

let manualFetalMarker = [];
let automatedFetalMarker = [];

// Try NEW structure first (fetal_movement as separate param)
const fetalMovementParam = keyardata?.find(
  (val) => val.param_type === "fetal_movement"
);

if (
  fetalMovementParam &&
  fetalMovementParam.data_set &&
  Object.keys(fetalMovementParam.data_set).length > 0
) {
  // NEW STRUCTURE: fetal_movement is a separate param_type
  Object.entries(fetalMovementParam.data_set).forEach(([ts, val]) => {
    const secTs = normalizeToSeconds(ts);
    if (!secTs) return;

    const v = Number(val);
    if (Number.isNaN(v)) return;

    if (v === 0) {
      manualFetalMarker.push([secTs, 205]);
    } else if (v === 1) {
      automatedFetalMarker.push([secTs, 205]);
    }
  });
} else {

  const fhrMetaData = keyardata?.find(
    (val) => val.param_type === "fhr"
  )?.meta_data;
  const fetalMarker = fhrMetaData?.fetal_movement || [];

  if (typeof fetalMarker[0] === "object") {
  
    fetalMarker.forEach((item) => {
      const secTs = normalizeToSeconds(item.timestamp);
      if (!secTs) return;

      if (item.type === "automated") {
        automatedFetalMarker.push([secTs, 205]);
      } else {
        manualFetalMarker.push([secTs, 205]);
      }
    });
  } else {
  
    fetalMarker.forEach((ts) => {
      const secTs = normalizeToSeconds(ts);
      if (secTs) {
        manualFetalMarker.push([secTs, 205]);
      }
    });
  }
}

setAfm(automatedFetalMarker);
setFmm(manualFetalMarker);

    const sortedFhr = sortTimestamp(fhrData);
    const sortedMhr = sortTimestamp(mhrData);
    const sortedUc = sortTimestamp(ucData);
    const sortedFhr2 = sortTimestamp(fhr2Data);
    const sortedSpo2 = sortTimestamp(spo2Data);

    let fhr = Object.entries(sortedFhr).map(([key, value]) => [
      String(key).length == "13" ? Number(key / 1000) : Number(key * 1000),
      value == 0 ? null : Number(value),
    ]);
    let fhr2 = Object.entries(sortedFhr2).map(([key, value]) => [
      String(key).length == "13" ? Number(key / 1000) : Number(key * 1000),
      value == 0 ? null : Number(value),
    ]);
    let mhr = Object.entries(sortedMhr).map(([key, value]) => [
      String(key).length == "13" ? Number(key / 1000) : Number(key * 1000),
      value == 0 ? null : Number(value),
    ]);
    let uc = Object.entries(sortedUc).map(([key, value]) => [
      String(key).length == "13" ? Number(key / 1000) : Number(key * 1000),
      value == 0 ? null : Number(value),
    ]);
    let spo2 = Object.entries(sortedSpo2).map(([key, value]) => [
      String(key).length == "13" ? Number(key / 1000) : Number(key * 1000),
      value == 0 ? null : Number(value),
    ]);

    setFHr(fhr);
    setFHR2(fhr2);
    setMHR(mhr);
    setUC(uc);
    setSpo2(spo2);
    uc.sort((a, b) => a[0] - b[0]);
    // console.log(uc,"UC")
    const lastUcTimestamp = uc && uc.length > 0 && uc[uc.length - 1][0];
    let temp_difference;
    if (lastUcTimestamp) {
      temp_difference = lastUcTimestamp * 1000 - startTime * 1000;
    }

    // console.log(temp_difference,"Difference",lastUcTimestamp,"Last UC TimeStamp",start_time,"Start Time")

    setDifference(temp_difference);

    //console.log(sortedFhr, 'fhr')

    var currentdate = new Date().toLocaleString();
    let ampm = new Date().getHours() >= 12 ? "PM" : "AM";
    let generatedDate = currentdate + " " + ampm;
    setGeneratedTime(generatedDate);
  }, [data]);

  useEffect(() => {
    if (placeDetail) {
      const placeId = Object.keys(placeDetail)[0];
      const place = placeDetail[placeId];
      if (place) {
        const place_configuration =
          place?.configurations?.fetal_algorithm_enabled;
        setInterpretationDisable(place_configuration);
      }
    }
  }, [placeDetail]);

  // The recording can be many hours long (the whole night); show a fixed
  // 20 min window and let the user scroll through the rest. Scrolling is done
  // straight on the Highcharts axes (no React state while scrolling, so the
  // scrollbar isn't re-created mid-drag) and mirrored onto the other chart.
  const VISIBLE_WINDOW_SECONDS = 20 * 60;
  const [, setSessionReset] = useState(0);
  const windowStartRef = useRef(null);
  const fhrChartRef = useRef(null);
  const ucChartRef = useRef(null);
  const graphWrapperRef = useRef(null);

  const getDataMax = () => {
    const lastX = (series) => (series.length ? series[series.length - 1][0] : 0);
    return Math.max(lastX(fhr), lastX(uc), startTime || 0);
  };

  // Move both charts to the window starting at `start` (skipping `source`,
  // which is already there when it triggered the move).
  const moveWindow = (start, source) => {
    windowStartRef.current = start;
    [fhrChartRef.current, ucChartRef.current].forEach((chart) => {
      if (!chart || chart === source) return;
      chart.xAxis[0].setExtremes(
        start,
        start + VISIBLE_WINDOW_SECONDS,
        true,
        false,
        { trigger: "syncExtremes" },
      );
    });
  };

  // New session / new data: go back to the start of the recording
  // (only when the session itself changes, not when the same session's data
  // is refreshed, so the view never jumps by itself)
  const lastSessionStartRef = useRef(null);
  useEffect(() => {
    if (!startTime || lastSessionStartRef.current === startTime) return;
    lastSessionStartRef.current = startTime;
    moveWindow(startTime);
    setSessionReset((n) => n + 1); // re-render so options carry the new window
  }, [startTime]);

  // Only react to real user gestures; programmatic updates (our own sync,
  // chart.update on re-render) must not feed back into the window position.
  const USER_TRIGGERS = ["pan", "scrollbar", "navigator", "touchpan", "zoom"];
  const afterSetExtremes = (chartRef) => (e) => {
    if (!USER_TRIGGERS.includes(e.trigger) || e.min == null) return;
    moveWindow(e.min, chartRef.current);
  };

  // Horizontal wheel / trackpad swipe (or shift + wheel) over the graphs
  // scrolls along the recording; vertical wheel keeps scrolling the page.
  // (It never zooms.)
  useEffect(() => {
    const el = graphWrapperRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey;
      if (!horizontal || windowStartRef.current == null) return;
      e.preventDefault();
      const raw =
        (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) * 0.2;
      // cap the step so trackpad inertia can't fling the view across the night
      const maxStep = VISIBLE_WINDOW_SECONDS / 40;
      const delta = Math.max(-maxStep, Math.min(maxStep, raw));
      const maxStart = Math.max(getDataMax() - VISIBLE_WINDOW_SECONDS, startTime);
      moveWindow(
        Math.min(Math.max(windowStartRef.current + delta, startTime), maxStart),
      );
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  });

  // Touch: a horizontal swipe on either graph scrolls along the recording,
  // a vertical swipe scrolls the page as usual (the wrapper is touch-action:
  // pan-y, see keyar-layout.scss). Highcharts' own touch panning is blocked
  // because it would swallow vertical swipes too.
  useEffect(() => {
    const el = graphWrapperRef.current;
    if (!el) return undefined;
    let touch = null;
    const onTouchStart = (e) => {
      touch =
        e.touches.length === 1
          ? { x: e.touches[0].clientX, y: e.touches[0].clientY, axis: null }
          : null;
    };
    const onTouchMove = (e) => {
      e.stopPropagation(); // keep Highcharts from panning/preventing scroll
      if (!touch || e.touches.length !== 1 || windowStartRef.current == null) return;
      const dx = e.touches[0].clientX - touch.x;
      const dy = e.touches[0].clientY - touch.y;
      if (!touch.axis) {
        if (Math.abs(dx) + Math.abs(dy) < 8) return;
        touch.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      }
      if (touch.axis !== "x") return;
      const plotWidth = fhrChartRef.current?.plotWidth || el.clientWidth || 1;
      const delta = (-dx * VISIBLE_WINDOW_SECONDS) / plotWidth;
      touch.x = e.touches[0].clientX;
      const maxStart = Math.max(getDataMax() - VISIBLE_WINDOW_SECONDS, startTime);
      moveWindow(
        Math.min(Math.max(windowStartRef.current + delta, startTime), maxStart),
      );
    };
    const onTouchEnd = () => {
      touch = null;
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { capture: true, passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove, { capture: true });
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  });

  const stockChartCommon = {
    navigator: { enabled: false },
    rangeSelector: { enabled: false },
    plotOptions: {
      series: {
        dataGrouping: { enabled: false },
        // tooltip only while hovering a point/line, not anywhere on the plot
        stickyTracking: false,
      },
    },
    // Lavender scrollbar (dashboard theme colour), one under each chart
    scrollbar: {
      enabled: true,
      height: 14,
      margin: 8,
      buttonsEnabled: false,
      barBackgroundColor: "#A163F1",
      barBorderColor: "#A163F1",
      barBorderRadius: 7,
      barBorderWidth: 0,
      rifleColor: "#FFFFFF",
      trackBackgroundColor: "#F7F0FF",
      trackBorderColor: "#E3D3FB",
      trackBorderRadius: 7,
      trackBorderWidth: 1,
    },
  };

  const commonXAxis = {
    ordinal: false,
    // fall back to the session start so the very first paint is already the
    // first 20 min window (not the whole night squeezed in for a moment)
    min: windowStartRef.current ?? (startTime || undefined),
    max:
      (windowStartRef.current ?? startTime)
        ? (windowStartRef.current ?? startTime) + VISIBLE_WINDOW_SECONDS
        : undefined,
    scrollbar: { enabled: false },
    crosshair: false, // Highstock draws a vertical crosshair by default
    // Same as CMS: major lines every 3 min anchored to a whole clock minute
    // (not to the first point's seconds)
    tickPositioner: function () {
      if (!startTime) return this.tickPositions;
      const interval = 180;
      const origin = Math.ceil(startTime / 60) * 60;
      let tick = origin + Math.max(0, Math.ceil((this.min - origin) / interval)) * interval;
      const positions = [];
      while (tick <= this.max) {
        positions.push(tick);
        tick += interval;
      }
      return positions;
    },
  };

  var chartMargin = 70;
  var legendFont = 10;
  var markerRadius = 8;
  var lineWidth = traceThickness;
  var alertWidth = 2;
  var itemMargin = 2;

  let chartHeightForFHRMHR;
  let chartHeightForUC;
  if (window.outerHeight < 950 && window.outerWidth < 1450) {
    chartHeightForFHRMHR = "60%"; // 60
    chartHeightForUC = "75%"; // 35
  } else if (window.outerHeight >= 950 && window.outerWidth >= 1450) {
    chartHeightForUC = "25%"; // 25
    chartHeightForFHRMHR = "30%"; // 40
  }

  // const printNST = () => {
  //   setDownload(true)

  //   setisLoading(true);
  //   //window.print()
  //   // document.getElementById('keyar_graph_document_header').style.display = "block";
  //   // document.getElementById('keyar_interpretation_block').style.display = "block";
  //   const inputText = document.getElementById('download-nst-report');

  //   console.log(inputText.style.width)
  //   const inputArea = document.getElementById('graph')

  //   html2canvas(inputText, { scale: 1, useCORS: true})
  //   .then(canvas => {
  //       let imgWidth = 400;
  //       let imgHeight = canvas.height * imgWidth / canvas.width;

  //       const imgData = canvas.toDataURL('img/png');
  //       const pdf = new jsPDF('l', 'mm', 'a4');
  //       pdf.addImage(imgData, 'PNG', 0, 0,  304, 223);
  //       window.open(pdf.output("bloburl"), "_blank");
  //       pdf.save('id'+"_keyar_report.pdf");
  //       setisLoading(false);
  //       //document.getElementById('keyar_graph_document_header').style.display = "none";
  //       //keyarGraphs(basicDetail.keyar_data, 'all', false, false, setdataTimes, state.startTime)
  //   })
  // }

  //   const printNST = () => {
  //     setDownload(true);
  //     setisLoading(true);
  //     const inputText = document.getElementById('download-nst-report');
  //     const inputArea = document.getElementById('graph');

  //     htmlToImage.toPng(inputText)
  //     .then((dataUrl) => {
  //         let img = new Image();
  //         img.src = dataUrl;
  //         img.onload = () => {
  //             let imgWidth = 304;
  //             let imgHeight = img.height * imgWidth / img.width;
  //             const pdf = new jsPDF('l', 'mm', 'a4');

  //             pdf.addImage(dataUrl, 'PNG', 0, 0, imgWidth, 220);
  //             window.open(pdf.output("bloburl"), "_blank");
  //            // pdf.save('id' + "_keyar_report.pdf");
  //             setisLoading(false);
  //         };
  //     })
  //     .catch((error) => {
  //         console.error('Error generating PDF:', error);
  //         setisLoading(false);
  //     });
  // }

  const printNST = () => {
    const inputText = document.getElementById("download-nst-report");
    if (!inputText) return;

    setisLoading(true);

    // Opened synchronously on click, else the browser blocks a tab opened after the async render
    const pdfTab = window.open("", "_blank");
    pdfTab?.document.write(
      '<title>Generating report...</title><style>body{margin:0;height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;font-family:sans-serif;color:#555}' +
        '.s{width:44px;height:44px;border:4px solid #e3d9fb;border-top-color:#7c4dff;border-radius:50%;animation:r 1s linear infinite}@keyframes r{to{transform:rotate(360deg)}}</style>' +
        '<div class="s"></div><div>Generating your NST report...</div>'
    );

    const navbar = document.querySelector(".top-gradient-navbar");

    let clonedNavbar = null;

    // 🧼 Clean screen styles
    const originalBodyMargin = document.body.style.margin;
    const originalOverflow = document.body.style.overflow;
    document.body.style.margin = "0";
    document.body.style.overflow = "hidden";

    if (navbar) navbar.style.visibility = "hidden";
    inputText.classList.add("nst-printing");

    if (navbar && inputText) {
      clonedNavbar = navbar.cloneNode(true);
      clonedNavbar.classList.add("print-navbar");
      clonedNavbar.style.visibility = "visible";
      inputText.prepend(clonedNavbar);
    }

    // Wait for all images to load
    const images = inputText.getElementsByTagName("img");
    const imagePromises = Array.from(images).map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = resolve; // Resolve even on error to not block
        // Set a timeout as backup
        setTimeout(resolve, 3000);
      });
    });

    Promise.all(imagePromises)
      .then(async () => {
        const margin = 5;
        const pdf = new jsPDF("l", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const originalStart = windowStartRef.current;
        const fhrChart = fhrChartRef.current;
        const ucChart = ucChartRef.current;
        // One page per 20 min of recording; header, details and
        // interpretation repeat on every page.
        const pages = startTime
          ? Math.max(1, Math.ceil((getDataMax() - startTime) / VISIBLE_WINDOW_SECONDS))
          : 1;

        // HTML-rendered chart text lives outside the chart's <svg>; use plain
        // SVG text while printing so the SVG snapshots below are complete.
        ucChart.update({
          legend: {
            useHTML: false,
            labelFormatter: function () {
              return this.name.replace("SpO2", "SpO\u2082");
            },
          },
          yAxis: { title: { useHTML: false, text: "Uterine Activity & SpO\u2082" } },
        });
        try {
          if (startTime) moveWindow(startTime);
          // The report is screenshotted ONCE (header, details, interpretation);
          // each page then only swaps in the graphs for its 20 min window,
          // rasterised from the charts' SVG, which is much faster than
          // screenshotting the whole report per page.
          const base = await htmlToImage.toCanvas(inputText, {
            backgroundColor: "#ffffff",
            pixelRatio: 2,
            cacheBust: true, // Important for base64 images
            style: { margin: "0" },
          });
          const host = inputText.getBoundingClientRect();
          const scale = base.width / host.width;
          const place = (el) => {
            const r = el.getBoundingClientRect();
            return [(r.left - host.left) * scale, (r.top - host.top) * scale, r.width * scale, r.height * scale];
          };
          const wrapperBox = place(graphWrapperRef.current);
          const chartBoxes = [fhrChart, ucChart].map((c) => place(c.container));

          const svgToImage = (chart) =>
            new Promise((resolve) => {
              const svg = chart.container.querySelector("svg").cloneNode(true);
              svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
              svg.querySelectorAll(".highcharts-scrollbar").forEach((n) => n.remove());
              const img = new Image();
              img.onload = () => resolve(img);
              img.onerror = () => resolve(null);
              img.src =
                "data:image/svg+xml;charset=utf-8," +
                encodeURIComponent(new XMLSerializer().serializeToString(svg));
            });

          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = base.width;
          pageCanvas.height = base.height;
          const ctx = pageCanvas.getContext("2d");
          for (let i = 0; i < pages; i++) {
            if (i > 0) pdf.addPage();
            if (startTime) moveWindow(startTime + i * VISIBLE_WINDOW_SECONDS);
            const imgs = await Promise.all([fhrChart, ucChart].map(svgToImage));
            ctx.drawImage(base, 0, 0);
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(...wrapperBox);
            imgs.forEach((img, k) => img && ctx.drawImage(img, ...chartBoxes[k]));
            // Never taller than A4, never stretched: a short (single-FHR) report keeps its natural height
            const imgW = pdfWidth - margin * 2;
            const imgH = Math.min((imgW * pageCanvas.height) / pageCanvas.width, pdf.internal.pageSize.getHeight() - 2);
            pdf.addImage(pageCanvas.toDataURL("image/jpeg", 0.92), "JPEG", margin, 0, imgW, imgH);
          }
        } finally {
          if (startTime) moveWindow(originalStart ?? startTime);
        }
        const url = pdf.output("bloburl");
        if (pdfTab && !pdfTab.closed) pdfTab.location.href = url;
        else window.open(url, "_self");
      })
      .catch((error) => {
        console.error("Error generating PDF:", error);
        pdfTab?.close();
      })
      .finally(() => {
        if (clonedNavbar) clonedNavbar.remove();
        inputText.classList.remove("nst-printing");
        if (navbar) navbar.style.visibility = "visible";

        // 🔄 restore styles
        document.body.style.margin = originalBodyMargin;
        document.body.style.overflow = originalOverflow;

        setisLoading(false);
      });
  };

  Highcharts.setOptions({
    global: {
      useUTC: false,
      useHTML: true,
    },
  });

  const fhrGraph = {
    ...stockChartCommon,
    chart: {
      // + scrollbar height so the plot area keeps its original size
      height: 520,
      zoomType: undefined,
      // Highstock zooms on mouse wheel / pinch by default; we only want panning
      zooming: { type: undefined, mouseWheel: { enabled: false } },
      pinchType: undefined,
      panning: { enabled: true, type: "x" },
      panKey: undefined,
    },
    credits: {
      enabled: false,
    },
    // mapNavigation: {
    //   zoomIn: true,
    //   zoomOut: true
    // },
    time: {
      useUTC: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      type: "Time",
      dateTimeLabelFormats: {
        millisecond: "%H:%M:%S.%L",
        second: "%H:%M:%S",
        minute: "%H:%M",
        hour: "%H:%M",
        day: "%e %b",
        week: "%e %b",
        month: "%b '%y",
        year: "%Y",
      },
      ...commonXAxis,
      events: { afterSetExtremes: afterSetExtremes(fhrChartRef) },
      tickInterval: 180,
      minorTickInterval: 30,
      minorGridLineColor: `rgba(0, 0, 0, 0.2)`,
      gridLineColor: "rgba(0, 0, 0)",
      gridLineWidth: 0.7,

      labels: {
        formatter: function () {
          // Same as CMS: a line labelled HH:MM marks the END of that minute,
          // so points shown as HH:MM sit to the left of the line.
          return getReadableTimeOnly(parseInt(this.value) - 60);
        },
        // step: getStep(),
      },
      title: {
        text: "Time",
      },
    },
    yAxis: {
      opposite: false,
      crosshair: false,
      title: {
        text: "FHR, MHR, Fetal Marker",
        style: {
          fontFamily: "Poppins, sans-serif",
          fontSize: "14px",
          fontWeight: "500",
        },
      },

      plotBands: [
        {
          color: `rgba(183,178,255,0.25)`,
          gridLineColor: `white`,
          from: 110,
          to: 160,
          zIndex: -1,
        },
      ],
      tickInterval: 20,
      minorTickInterval: 5,
      // minorTickInterval: 10,
      min: 50,
      max: 210,
      startOnTick: false,
      endOnTick: false,
      minorGridLineColor: `rgba(0, 0, 0, 0.2)`,
      gridLineColor: "rgba(0, 0, 0)",
      gridLineWidth: 0.7,
    },
    tooltip: {
      split: false,
      shared: false,
      // formatter: function(){
      //     var val = this;
      //     return tooltipFormat(val);
      // }
      formatter: function () {
        return (
          "<b>" +
          this.series.name +
          "</b><br/>" +
          getReadableTime(parseInt(this.key)) +
          "<br/>" +
          this.y
        );
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "top",
      layout: "horizontal",
      itemDistance: 50,
      zIndex: 200,
      borderWidth: 1,
      borderColor: "#000000",
      backgroundColor: "#FFFFFF",
      itemStyle: {
        fontFamily: "Poppins, sans-serif",
        cursor: "pointer",
        fontSize: "12px",
      },
    },

    series: [
      {
        name: "Fetal Heart Rate",
        data: fhr,
        lineWidth: lineWidth,
        marker: {
          //radius : markerRadius,
          symbol: "url(/mhr-symbol.svg)",
        },
        color: "rgb(254,141,7)",
        zIndex: 5,
      },
      {
        name: "Maternal Heart Rate",
        data: mhr,
        lineWidth: lineWidth,
        marker: {
          //radius : markerRadius,
          symbol: "url(/fhr-symbol.svg)",
        },
        color: "rgb(239, 55, 151)",
        zIndex: 5,
      },
      {
        name: "Fetal Heart Rate 2",
        data: fhr2,
        lineWidth: lineWidth,
        marker: {
          //radius : markerRadius,
          symbol: "circle",
        },
        color: "#2680EB",
        zIndex: 5,
      },
      {
        name: "Manual Fetal Marker",
        data: fmm,
        type: "scatter",
        marker: {
          radius: 5,
          symbol: "circle",
          fillColor: "rgb(239, 55, 151)",
        },
        color: "rgb(239, 55, 151)",
        zIndex: 5,
      },
      {
        name: "Automated Fetal Marker",
        data: afm,
        type: "scatter",
        marker: {
          radius: 5,
          symbol: "circle",
          fillColor: "white",
          lineWidth: 2,
          lineColor: "rgb(239, 55, 151)",
        },
        color: "rgb(239, 55, 151)",
        zIndex: 5,
      },
    ],
  };

  const ucGraph = {
    ...stockChartCommon,
    chart: {
      height: 366,
      zoomType: undefined,
      // Highstock zooms on mouse wheel / pinch by default; we only want panning
      zooming: { type: undefined, mouseWheel: { enabled: false } },
      pinchType: undefined,
      panning: { enabled: true, type: "x" },
      panKey: undefined,
    },
    credits: {
      enabled: false,
    },
    time: {
      useUTC: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      type: "Time",
      dateTimeLabelFormats: {
        millisecond: "%H:%M:%S.%L",
        second: "%H:%M:%S",
        minute: "%H:%M",
        hour: "%H:%M",
        day: "%e %b",
        week: "%e %b",
        month: "%b '%y",
        year: "%Y",
      },
      ...commonXAxis,
      events: { afterSetExtremes: afterSetExtremes(ucChartRef) },
      gridLineColor: "rgba(0, 0, 0)",
      gridLineWidth: 0.7,
      minorGridLineColor: `rgba(0, 0, 0, 0.3)`,

      tickInterval: 180,
      minorTickInterval: 30,
      labels: {
        formatter: function () {
          // Same as CMS: a line labelled HH:MM marks the END of that minute,
          // so points shown as HH:MM sit to the left of the line.
          return getReadableTimeOnly(parseInt(this.value) - 60);
        },
        // step: getStep(),
      },
      title: {
        text: "Time",
      },
    },
    yAxis: {
      opposite: false,
      crosshair: false,
      title: {
        text: "Uterine Activity & SpO<sub>2</sub>",
        useHTML: true, // Enable HTML for title
        style: {
          fontFamily: "Poppins, sans-serif",
          fontSize: "14px",
          fontWeight: "500",
        },
      },
      tickInterval: 20,
      minorTickInterval: 10,
      minorGridLineColor: `rgba(0, 0, 0, 0.3)`,
      gridLineColor: "#000000",
      gridLineWidth: 0.7,
      min: 0,
      max: 100,
    },
    tooltip: {
      split: false,
      shared: false,
      // formatter: function(){
      //     var val = this;
      //     return tooltipFormat(val);
      // }
      formatter: function () {
        return (
          "<b>" +
          this.series.name +
          "</b><br/>" +
          getReadableTime(parseInt(this.key)) +
          "<br/>" +
          parseFloat(parseFloat(this.y).toFixed(2))
        );
      },
    },
    legend: {
      enabled: true,
      align: "right",
      verticalAlign: "top",
      layout: "horizontal",
      itemDistance: 25,
      zIndex: 200,
      borderWidth: 1,
      borderColor: "#000000",
      backgroundColor: "#FFFFFF",
      itemStyle: {
        fontFamily: "Poppins, sans-serif",
        cursor: "pointer",
        fontSize: "12px",
      },
      labelFormatter: function () {
        return this.name.replace("SpO2", "SpO<sub>2</sub>"); // Format SpO2 with subscript
      },
      useHTML: true,
    },
    series: [
      {
        name: "Uterine Contractions",
        data: uc,
        lineWidth: lineWidth,
        marker: {
          //radius : markerRadius,
          symbol: "circle ccc",
        },
        color: "rgb(239, 55, 151)",
      },
      {
        name: "SpO2",
        data: spo2,
        lineWidth: lineWidth,
        marker: {
          //radius : markerRadius,
          symbol: "circle",
        },
        color: "#2680EB",
      },
    ],
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "p") {
      e.preventDefault();
      alert("Please click on Print button to print the NST report");
    }
  };

  useEffect(() => {
    const handleKeyDownEvent = (e) => {
      handleKeyDown(e);
    };

    window.addEventListener("keydown", handleKeyDownEvent);

    return () => {
      window.removeEventListener("keydown", handleKeyDownEvent);
    };
  }, []);

  useEffect(() => {
    const cookieData = Cookie.get("data");

    if (cookieData) {
      const parsed = JSON.parse(cookieData);

      const cookieLogo = parsed.logo; // base64 string or empty
      const cookieHospital = parsed.hospitalName; // fallback hospital name

      // Update context ONLY when empty after refresh
      if (!finalLogo.value) {
        if (cookieLogo) {
          setLogo(cookieLogo);
        } else if (cookieHospital) {
          setHospitalName(cookieHospital);
        }
      }
    }
  }, []);

  return (
    <>
      {/* Covers the page while the PDF is generated, so the graphs moving
          underneath (one window per page) are not visible */}
      {isLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            background: "rgba(255,255,255,0.95)",
          }}
        >
          <Loading text="Generating PDF, please wait...." />
        </div>
      )}
      <div id="nst-report">
        <div className="reports-detail-div">
          <p className="reports-detail-text">
            Reports {`>`} {basicDetail.name} ({value}) {`>`} Device Data{" "}
            {`>`}{" "}
          </p>
          <p className="device-name"> {deviceName} </p>
        </div>

        <div className="print-settings">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              marginTop: 0,
              fontFamily: "Poppins",
            }}
          >
            <FormControl
              sx={{
                minWidth: "11rem",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "0.5px solid #A163F1",
                  "&.Mui-focused": { borderColor: "#A163F1" },
                },
              }}
            >
              {isUserSelected && (
                <InputLabel
                  sx={{
                    color: "#000000",
                    fontFamily: "Poppins",
                    fontSize: "1rem",
                    transition: "all 1s ease-in-out",
                    "&.Mui-focused": { color: "#000000" },
                  }}
                >
                  Trace Thickness
                </InputLabel>
              )}

              <Select
                value={traceThickness}
                onChange={handleTraceThicknessChange}
                displayEmpty
                renderValue={(selected) =>
                  !isUserSelected ? "Trace Thickness" : selected.toFixed(2)
                }
                sx={{
                  borderRadius: "8px",
                  height: "2.5rem",
                  background: "#FFFFFF !important",
                  fontFamily: "Poppins",
                  marginTop: 0,
                  "& .MuiSelect-icon": {
                    color: "black",
                  },
                  "&:hover": { border: "0.5px solid #A163F1" },
                  "& .MuiSelect-select": {
                    fontSize: "1rem",
                    fontWeight: "400",
                  },
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      marginTop: "0.2rem",
                      backgroundColor: "#faf6ff !important",
                    },
                  },
                }}
              >
                {tracethicknessOptions.map((value) => (
                  <MenuItem
                    key={value}
                    value={parseFloat(value)}
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      "&:hover": {
                        backgroundColor: "#faf6ff !important",
                        fontFamily: "Poppins",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#faf6ff !important",
                        marginTop: "0.5rem",
                        fontFamily: "Poppins",
                      },
                    }}
                  >
                    <ListItemText
                      primary={value}
                      primaryTypographyProps={{
                        style: { fontFamily: "Poppins" },
                      }}
                    />

                    {parseFloat(value) === traceThickness && (
                      <CheckIcon
                        sx={{
                          color: "#6BBF59",
                          width: "1.5rem",
                          height: "1.5rem",
                        }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <div className="keyar-layout-button-div" onClick={() => printNST()}>
            <img
              src="/white_print.svg"
              alt="Print Icon"
              width={20}
              height={20}
            />
            <p className="print-nst-button">Print</p>
          </div>
        </div>
      </div>
      {/* 🔥 TOP GRADIENT NAV BAR WITH JANITRI LOGO */}

      <div className="top-gradient-navbar">
        <img
          src="/janitri-registered-logo.svg"
          alt="Janitri Logo"
          className="janitri-main-logo"
        />
      </div>
      {selectedSubTab ? (
        <div>
          {isKeyarEcho && selectedSubTab === "Recordings" ? (
            fileURL != undefined &&
            fileURL != "" && (
              <div className="keyar-echo-div">
                <div className="upper-div">
                  <p className="avg-fhr">Average FHR: 130</p>
                  <p className="duration">Duration: 04:23</p>
                </div>

                <div className="audio">
                  <p className="fhr-key">FHR</p>
                  <p className="fhr-value">120</p>
                  <AudioPlayer
                    fileURL={fileURL}
                    isPlaying={isPlaying}
                    setIsPlaying={setIsPlaying}
                  />
                </div>
              </div>
            )
          ) : (isKeyarDT || isKeyarCM) && selectedSubTab === "NST" ? (
            <div id="download-nst-report" className="nst-report">
              <div id="basic-detail">
                <Grid container spacing={2}>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      padding: "15px 0px 10px 30px",
                    }}
                  >
                    <Grid item xs={2}>
                      {finalLogo?.isImage && finalLogo?.value ? (
                        <img
                          src={finalLogo.value}
                          alt="Hospital Logo"
                          className="hospital_nst_logo"
                        />
                      ) : (
                        <p className="hospital_nst_fallback">
                          {finalLogo?.value && finalLogo.value.trim() !== ""
                            ? finalLogo.value
                            : Cookie.get("hospital_detail")
                              ? JSON.parse(Cookie.get("hospital_detail"))
                                  ?.hospitalName || "--"
                              : "--"}
                        </p>
                      )}
                    </Grid>
                    <Grid item xs={10}>
                      <Grid container spacing={1}>
                        <Grid item xs={2} sm={4} md={4}>
                          <div className="basic_detail_row">
                            <p className="basic_detail_name"> Name</p>
                            <p className="basic_detail_field">:</p>
                            <p className="basic_detail_field">
                              {basicDetail.name || "--"}
                            </p>
                          </div>
                        </Grid>
                        <Grid item xs={2} sm={4} md={4}>
                          <div className="basic_detail_row">
                            <p className="basic_detail_name"> Presentation</p>
                            <p className="basic_detail_field">:</p>
                            <p className="basic_detail_field">
                              {" "}
                              {otherDetail.presentation || "--"}
                            </p>
                          </div>
                        </Grid>
                        <Grid item xs={2} sm={4} md={4}>
                          <div className="basic_detail_row">
                            <p className="basic_detail_name">{label}</p>
                            <p className="basic_detail_field">:</p>
                            <p className="basic_detail_field">{value}</p>
                          </div>
                        </Grid>
                        <Grid item xs={2} sm={4} md={4}>
                          <div className="basic_detail_row">
                            <p className="basic_detail_name"> Age</p>
                            <p className="basic_detail_field">:</p>
                            <p className="basic_detail_field">
                              {basicDetail.age || "--"}
                            </p>
                          </div>
                        </Grid>
                        <Grid item xs={2} sm={4} md={4}>
                          <div className="basic_detail_row">
                            <p className="basic_detail_name">
                              {" "}
                              Number of fetus
                            </p>
                            <p className="basic_detail_field"> :</p>
                            <p className="basic_detail_field">
                              {interpretationData.fhr2_signal_present === false
                                ? "Singleton/Single"
                                : "Twin"}
                            </p>
                          </div>
                        </Grid>
                        <Grid item xs={2} sm={4} md={4}>
                          <div className="basic_detail_row">
                            <p className="basic_detail_name"> Device Used</p>
                            <p className="basic_detail_field"> :</p>
                            <p className="basic_detail_field">
                              {deviceName || "--"}
                            </p>
                          </div>
                        </Grid>
                        <Grid item xs={2} sm={4} md={4}>
                          <div className="basic_detail_row">
                            <p className="basic_detail_name">
                              {" "}
                              Gestational Age
                            </p>
                            <p className="basic_detail_field">:</p>
                            <p className="basic_detail_field">
                              {otherDetail.gestation_period || "--"}
                            </p>
                          </div>
                        </Grid>
                        <Grid item xs={2} sm={4} md={4}>
                          <div className="basic_detail_row">
                            <p className="basic_detail_name"> Time </p>
                            <p className="basic_detail_field">:</p>
                            <p className="basic_detail_field">
                              {getReadableTime(data.data_time)}
                            </p>
                          </div>
                        </Grid>
                        <Grid item xs={2} sm={4} md={4}>
                          <div className="basic_detail_row">
                            <p className="basic_detail_name">
                              {" "}
                              Total Monitoring Time{" "}
                            </p>
                            <p className="basic_detail_field">:</p>
                            <p className="basic_detail_field">
                              {`${Math.round(difference / 60000)} min` || "--"}
                            </p>
                          </div>
                        </Grid>
                      </Grid>
                    </Grid>
                  </div>
                </Grid>
                <hr className="nst-hr" />
              </div>
              <div className="keyar-layout">
                <div className="keyar-graphs-wrapper" style={{ width: "75%" }} ref={graphWrapperRef}>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={fhrGraph}
                    callback={(chart) => { fhrChartRef.current = chart; }}
                    constructorType={"stockChart"}
                    allowChartUpdate={true}
                  />
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={ucGraph}
                    callback={(chart) => { ucChartRef.current = chart; }}
                    constructorType={"stockChart"}
                    allowChartUpdate={true}
                  />
                </div>
                <Interpretation
                  interpretationData={interpretationData}
                  difference={difference}
                  interpretationDisable={interpretationDisable}
                  accountType={accountType}
                />
              </div>
            </div>
          ) : (
            <NoData />
          )}
        </div>
      ) : (
        <div>
          {isKeyarEcho ? (
            fileURL != undefined &&
            fileURL != "" && (
              <div className="keyar-echo-div">
                <div className="upper-div">
                  <p className="avg-fhr">Average FHR: 130</p>
                  <p className="duration">Duration: 04:23</p>
                </div>

                <div className="audio">
                  <p className="fhr-key">FHR</p>
                  <p className="fhr-value">120</p>
                  <AudioPlayer
                    fileURL={fileURL}
                    isPlaying={isPlaying}
                    setIsPlaying={setIsPlaying}
                  />
                </div>
              </div>
            )
          ) : isKeyarDT || isKeyarCM ? (
            <>
              <div id="download-nst-report" className="nst-report">
                <div id="basic-detail">
                  <Grid container spacing={2}>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        margin: "15px 0px 10px 30px",
                      }}
                    >
                      <Grid item xs={2}>
                        {finalLogo?.isImage && finalLogo?.value ? (
                          <img
                            src={finalLogo.value}
                            alt="Hospital Logo"
                            className="hospital_nst_logo"
                          />
                        ) : (
                          <p className="hospital_nst_fallback">
                            {finalLogo?.value && finalLogo.value.trim() !== ""
                              ? finalLogo.value
                              : Cookie.get("hospital_detail")
                                ? JSON.parse(Cookie.get("hospital_detail"))
                                    ?.hospitalName || "--"
                                : "--"}
                          </p>
                        )}
                      </Grid>
                      <Grid item xs={10}>
                        <Grid container spacing={1}>
                          <Grid item xs={2} sm={4} md={4}>
                            <div className="basic_detail_row">
                              <p className="basic_detail_name"> Name</p>
                              <p className="basic_detail_field">:</p>
                              <p className="basic_detail_field">
                                {basicDetail.name || "--"}
                              </p>
                            </div>
                          </Grid>
                          <Grid item xs={2} sm={4} md={4}>
                            <div className="basic_detail_row">
                              <p className="basic_detail_name"> Presentation</p>
                              <p className="basic_detail_field">:</p>
                              <p className="basic_detail_field">
                                {" "}
                                {otherDetail.presentation || "--"}
                              </p>
                            </div>
                          </Grid>
                          <Grid item xs={2} sm={4} md={4}>
                            <div className="basic_detail_row">
                              <p className="basic_detail_name">{label}</p>
                              <p className="basic_detail_field">:</p>
                              <p className="basic_detail_field">
                                {" "}
                                {value || "--"}
                              </p>
                            </div>
                          </Grid>
                          <Grid item xs={2} sm={4} md={4}>
                            <div className="basic_detail_row">
                              <p className="basic_detail_name"> Age</p>
                              <p className="basic_detail_field">:</p>
                              <p className="basic_detail_field">
                                {basicDetail.age || "--"}
                              </p>
                            </div>
                          </Grid>
                          <Grid item xs={2} sm={4} md={4}>
                            <div className="basic_detail_row">
                              <p className="basic_detail_name">
                                {" "}
                                Number of fetus
                              </p>
                              <p className="basic_detail_field"> :</p>
                              <p className="basic_detail_field">
                                {interpretationData.fhr2_signal_present ===
                                false
                                  ? "Singleton/Single"
                                  : "Twin"}
                              </p>
                            </div>
                          </Grid>
                          <Grid item xs={2} sm={4} md={4}>
                            <div className="basic_detail_row">
                              <p className="basic_detail_name"> Device Used</p>
                              <p className="basic_detail_field"> :</p>
                              <p className="basic_detail_field">
                                {deviceName || "--"}
                              </p>
                            </div>
                          </Grid>
                          <Grid item xs={2} sm={4} md={4}>
                            <div className="basic_detail_row">
                              <p className="basic_detail_name">
                                {" "}
                                Gestational Age
                              </p>
                              <p className="basic_detail_field">:</p>
                              <p className="basic_detail_field">
                                {otherDetail.gestation_period || "--"}
                              </p>
                            </div>
                          </Grid>
                          <Grid item xs={2} sm={4} md={4}>
                            <div className="basic_detail_row">
                              <p className="basic_detail_name"> Time </p>
                              <p className="basic_detail_field">:</p>
                              <p className="basic_detail_field">
                                {getReadableTime(data.data_time)}
                              </p>
                            </div>
                          </Grid>
                          <Grid item xs={2} sm={4} md={4}>
                            <div className="basic_detail_row">
                              <p className="basic_detail_name">
                                {" "}
                                Total Monitoring Time{" "}
                              </p>
                              <p className="basic_detail_field">:</p>
                              <p className="basic_detail_field">
                                {`${Math.round(difference / 60000)} min` ||
                                  "--"}
                              </p>
                            </div>
                          </Grid>
                        </Grid>
                      </Grid>
                    </div>
                  </Grid>
                  <hr className="nst-hr" />
                </div>
                <div className="keyar-layout">
                  <div className="keyar-graphs-wrapper" style={{ width: "75%" }} ref={graphWrapperRef}>
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={fhrGraph}
                    callback={(chart) => { fhrChartRef.current = chart; }}
                      constructorType={"stockChart"}
                      allowChartUpdate={true}
                    />
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={ucGraph}
                    callback={(chart) => { ucChartRef.current = chart; }}
                      constructorType={"stockChart"}
                      allowChartUpdate={true}
                    />
                  </div>
                  <Interpretation
                    interpretationData={interpretationData}
                    difference={difference}
                    interpretationDisable={interpretationDisable}
                    accountType={accountType}
                  />
                </div>
                {/* <p>Disclaimer: eCTG Interpretation does not provide medical advice; it is intended for information purposes only. It is not a substitute for professional medical advice, diagnosis and treatment.<span>NICE GUIDELINES</span></p> */}
              </div>
            </>
          ) : (
            <NoData />
          )}
        </div>
      )}
    </>
  );
};

export default KeyarGraph;
